import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { Users } from '../../../api/user';

/**
 * Creates an Excel file from employee data and initiates download
 * @param {Object} data - The API response containing employee data
 */
export const exportEmployeesToExcel = (data) => {
  if (!data || !data.users || !Array.isArray(data.users)) {
    throw new Error('Invalid data format');
  }

  const workbook = XLSX.utils.book_new();

  const headers = [
    'שם פרטי',
    'שם משפחה',
    'שם מלא',
    'תאריך לידה',
    'מספר טלפון',
    'תעודת זהות',
    'מזהה פנימי',
    'תאריך יצירה',
    'סניף',
    'סטטוס',
  ];

  // Transform the data for the Excel file
  const employeeRows = data.users.map((user) => {
    const fullName = user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim();
    const createdDate = user.createdDate
      ? new Date(user.createdDate).toLocaleDateString('he-IL')
      : '';

    return [
      user.firstName || '',
      user.lastName || '',
      fullName,
      user.details?.dob || '',
      user.phoneNumber || '',
      user.details?.govId || '',
      user.details?.internalEmployeeId || '',
      createdDate,
      user.details?.branch || '',
      Users.statusText(user.status),
    ];
  });

  const worksheet = XLSX.utils.aoa_to_sheet([headers, ...employeeRows]);

  // Right-to-left direction for Hebrew text
  worksheet['!rtl'] = true;

  XLSX.utils.book_append_sheet(workbook, worksheet, 'עובדים');

  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  const companyId = data.query?.companyId || 'unknown';
  const fileName = `employees_company_${companyId}_${new Date().toISOString().split('T')[0]}.xlsx`;

  saveAs(blob, fileName);
};

/**
 * Creates an Excel file from vault employee data (all employees in the vault, with registration status)
 * @param {Object} data - The API response containing vault employee data: { users, query: { companyId } }
 */
export const exportVaultEmployeesToExcel = (data) => {
  if (!data || !data.users || !Array.isArray(data.users)) {
    throw new Error('Invalid data format');
  }

  const workbook = XLSX.utils.book_new();

  const hasShiftData = data.users.some(
    (u) =>
      u.lastShiftDate !== undefined ||
      u.firstShiftDate !== undefined ||
      u.shiftDaysLast60 !== undefined
  );

  const sortedUsers = hasShiftData
    ? [...data.users].sort((a, b) => {
        const regDiff = (a.isRegistered ? 1 : 0) - (b.isRegistered ? 1 : 0);
        if (regDiff !== 0) return regDiff;
        return (b.lastShiftDate || '').localeCompare(a.lastShiftDate || '');
      })
    : data.users;

  const headers = [
    'שם פרטי',
    'שם משפחה',
    'שם מלא',
    'תאריך לידה',
    'מספר טלפון',
    'תעודת זהות',
    'מזהה פנימי',
    'תאריך יצירה',
    'סניף',
    'סטטוס',
    ...(hasShiftData
      ? ['תאריך משמרת ראשונה', 'תאריך משמרת אחרונה', 'ימי עבודה ב-60 יום האחרונים']
      : []),
  ];

  const employeeRows = sortedUsers.map((user) => {
    const fullName = user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim();
    const createdDate = user.createdDate
      ? new Date(user.createdDate).toLocaleDateString('he-IL')
      : '';
    const internalId = user.details?.internalEmployeeId || user.details?.internalId || '';
    const statusText =
      typeof user.isRegistered === 'boolean'
        ? user.isRegistered
          ? 'רשום'
          : 'לא רשום'
        : Users.statusText(user.status);
    const firstShiftDate = user.firstShiftDate
      ? new Date(user.firstShiftDate).toLocaleDateString('he-IL')
      : '';
    const lastShiftDate = user.lastShiftDate
      ? new Date(user.lastShiftDate).toLocaleDateString('he-IL')
      : '';
    const shiftDays = typeof user.shiftDaysLast60 === 'number' ? user.shiftDaysLast60 : '';

    return [
      user.firstName || '',
      user.lastName || '',
      fullName,
      user.details?.dob || '',
      user.phoneNumber || '',
      user.details?.govId || '',
      internalId,
      createdDate,
      user.details?.branch || '',
      statusText,
      ...(hasShiftData ? [firstShiftDate, lastShiftDate, shiftDays] : []),
    ];
  });

  const worksheet = XLSX.utils.aoa_to_sheet([headers, ...employeeRows]);
  worksheet['!rtl'] = true;
  XLSX.utils.book_append_sheet(workbook, worksheet, 'עובדים');

  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  const companyId = data.query?.companyId || 'unknown';
  const fileName = `vault_employees_company_${companyId}_${new Date().toISOString().split('T')[0]}.xlsx`;
  saveAs(blob, fileName);
};

/**
 * Creates an Excel file with only phonebook template headers (one row) for a company.
 * @param {string[]} headers - Column headers from the company's employee file transformer
 * @param {string|number} companyId - Company ID for the file name
 */
export const exportPhonebookTemplateToExcel = (headers, companyId) => {
  if (!headers || !Array.isArray(headers) || headers.length === 0) {
    throw new Error('Invalid headers');
  }

  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.aoa_to_sheet([headers]);
  worksheet['!rtl'] = true;
  XLSX.utils.book_append_sheet(workbook, worksheet, 'תבנית אלפון');

  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  const id = companyId ?? 'unknown';
  const fileName = `phonebook_template_company_${id}_${new Date().toISOString().split('T')[0]}.xlsx`;
  saveAs(blob, fileName);
};
